import React, { useState } from "react";
import { ChevronRight, ChevronLeft, Check, Pencil } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { zodFormResolver } from "@/util/form";
import { z } from "zod";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatNumber } from "@/util/sums";
import { userExistsByEmail } from "@/app/FintreeAPI";
import { usePostRegisterMutation } from "@/store/service";
import { ApiErrorResponse } from "@/store/axiosBaseQuery";
import { useAuth } from "@/context/AuthContext";
import RegisterData from "@/model/interface/RegisterData";
import ColFormValueSlider from "@/components/shared/ColFormValueSlider";
import { isDebugForms } from "@/util/debug";
import { registerPropertySchema, registerUserSchema, registerPasswordSchema } from "@/model/schemas";

const propertySchema = registerPropertySchema.extend({
  dateOfPropertyPurchase: z.coerce.date({ message: "Date of property purchase is required" }),
  estimatedOutstandingMortgage: z.number().min(1),
  yearsLeftOnMortgage: z.number().min(0),
  estimatedMonthlyMortgage: z.number().min(1),
});

const fullSchema = propertySchema.merge(registerUserSchema).merge(registerPasswordSchema);
type MortgageShareFormData = z.infer<typeof fullSchema>;

const MortgageShareRegister = () => {
  const { refreshAuthStatus } = useAuth();
  const [postRegister] = usePostRegisterMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [step, setStep] = useState<"property" | "user" | "password" | "success">("property");
  const [estimatedValue, setEstimatedValue] = useState(2500000);
  const [estimatedOutstandingMortgage, setEstimatedOutstandingMortgage] = useState(1250000);
  const [yearsLeftOnMortgage, setYearsLeftOnMortgage] = useState(0);
  const [estimatedMonthlyMortgage, setEstimatedMonthlyMortgage] = useState(1500);
  const [emailError, setEmailError] = useState<string | null>(null);

  const maxAmountValue = 10000000;
  const maxAmountMortgage = 10000000;
  const maxYears = 30;
  const maxMonthlyMortgage = 30000;
  const minAmountValue = 500000;
  const minAmountMortgage = 50000;
  const minYears = 0;
  const minMonthlyMortgage = 200;

  const {
    register,
    trigger,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<MortgageShareFormData>({
    resolver: zodFormResolver<MortgageShareFormData>(fullSchema),
    mode: "onTouched",
    defaultValues: {
      estimatedPropertyValue: 2500000,
      estimatedOutstandingMortgage: 1250000,
      yearsLeftOnMortgage: 0,
      estimatedMonthlyMortgage: 1500,
    },
  });

  const onSubmit = async (data: MortgageShareFormData) => {
    setSubmitError(null);
    try {
      await postRegister(data as RegisterData).unwrap();
      await refreshAuthStatus();
      setStep("success");
    } catch (error) {
      setSubmitError((error as Partial<ApiErrorResponse>).detail || "We couldn't create your account. Please try again.");
    }
  };

  const checkEmailExists = async (email: string) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) return;
    try {
      const response = await userExistsByEmail(email);
      if (response.status === 200) {
        setEmailError("Email already registered.");
        setError("email", { type: "manual", message: "Email already registered." });
      } else {
        setEmailError(null);
        clearErrors("email");
      }
    } catch {}
  };

  const advanceStep = async () => {
    if (step === "property") {
      setValue("estimatedPropertyValue", estimatedValue);
      setValue("estimatedOutstandingMortgage", estimatedOutstandingMortgage);
      setValue("yearsLeftOnMortgage", yearsLeftOnMortgage);
      setValue("estimatedMonthlyMortgage", estimatedMonthlyMortgage);
      if (isDebugForms()) {
        setStep("user");
        return;
      }
      const fields = Object.keys(propertySchema.shape) as (keyof typeof propertySchema.shape)[];
      const valid = await trigger(fields);
      if (valid) {
        setStep("user");
      }
    }

    if (step === "user") {
      if (isDebugForms()) {
        setStep("password");
        return;
      }
      const fields = Object.keys(registerUserSchema.shape) as (keyof typeof registerUserSchema.shape)[];
      const valid = await trigger(fields);
      if (valid && !emailError) {
        setStep("password");
      }
    }

    if (step === "password") {
      if (isDebugForms()) {
        setStep("success");
        return;
      }
      const valid = await trigger(["password"]);
      if (valid) {
        handleSubmit(onSubmit)();
      }
    }
  };

  const reverseStep = () => {
    if (step === "user") {
      setStep("property");
    }
    if (step === "password") {
      setStep("user");
    }
  };

  const passwordValue = watch("password") ?? "";

  const inputClass = (hasError: boolean) =>
    `w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${hasError ? "border-red-400" : "border-gray-300"}`;

  return (
    <div className="flex flex-col flex-1">
      <section className="relative text-center py-12 text-[#010305]">
        <h2 className="text-3xl font-bold text-gray-900">Monthly Boost</h2>
      </section>
      <section className="flex-1 pb-16">
        <div className="w-full max-w-2xl mx-auto px-4">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {step === "property" && (
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-gray-900">Property details</h3>

                <div className="relative">
                  <h6 className="text-sm text-gray-500 mb-1">Date of property purchase</h6>
                  <Controller
                    control={control}
                    name="dateOfPropertyPurchase"
                    render={({ field }) => (
                      <DatePicker
                        className={inputClass(!!errors.dateOfPropertyPurchase)}
                        placeholderText="Select date of property purchase"
                        onChange={(date) => field.onChange(date)}
                        selected={field.value}
                      />
                    )}
                  />
                  <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                  {errors.dateOfPropertyPurchase && (
                    <p className="mt-1 text-sm text-red-500">{errors.dateOfPropertyPurchase.message}</p>
                  )}
                </div>

                <ColFormValueSlider
                  title="Estimate the value of the property"
                  step={100000}
                  value={estimatedValue}
                  minAmount={minAmountValue}
                  maxAmount={maxAmountValue}
                  leftColumnValue={estimatedValue}
                  leftColumnFormatFunction={(v) => `$${formatNumber(v, 0, true)}`}
                  onChange={(amount) => setEstimatedValue(amount)}
                />

                <ColFormValueSlider
                  title="Estimated mortgage outstanding"
                  step={100000}
                  value={estimatedOutstandingMortgage}
                  minAmount={minAmountMortgage}
                  maxAmount={maxAmountMortgage}
                  leftColumnValue={estimatedOutstandingMortgage}
                  leftColumnFormatFunction={(v) => `$${formatNumber(v, 0, true)}`}
                  onChange={(amount) => setEstimatedOutstandingMortgage(amount)}
                />

                <ColFormValueSlider
                  title="Years left on mortgage"
                  step={1}
                  value={yearsLeftOnMortgage}
                  minAmount={minYears}
                  maxAmount={maxYears}
                  leftColumnValue={yearsLeftOnMortgage}
                  leftColumnFormatFunction={(v) => `${v}`}
                  onChange={(amount) => setYearsLeftOnMortgage(amount)}
                />

                <ColFormValueSlider
                  title="Estimated monthly mortgage repayment"
                  step={100}
                  value={estimatedMonthlyMortgage}
                  minAmount={minMonthlyMortgage}
                  maxAmount={maxMonthlyMortgage}
                  leftColumnValue={estimatedMonthlyMortgage}
                  leftColumnFormatFunction={(v) => `$${formatNumber(v, 0, true)}`}
                  onChange={(amount) => setEstimatedMonthlyMortgage(amount)}
                />

                <div className="relative">
                  <h6 className="text-sm text-gray-500 mb-1">Property address</h6>
                  <input
                    type="text"
                    className={inputClass(!!errors.propertyAddress)}
                    placeholder="Enter property address"
                    {...register("propertyAddress")}
                  />
                  <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                  {errors.propertyAddress && (
                    <p className="mt-1 text-sm text-red-500">{errors.propertyAddress.message}</p>
                  )}
                </div>
              </div>
            )}

            {step === "user" && (
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-gray-900">Personal details</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <h6 className="text-sm text-gray-500 mb-1">First name</h6>
                    <input
                      type="text"
                      {...register("firstName")}
                      className={inputClass(!!errors.firstName)}
                      placeholder="First Name"
                    />
                    <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                    {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName.message}</p>}
                  </div>
                  <div className="relative">
                    <h6 className="text-sm text-gray-500 mb-1">Last name</h6>
                    <input
                      type="text"
                      {...register("lastName")}
                      className={inputClass(!!errors.lastName)}
                      placeholder="Last Name"
                    />
                    <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                    {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName.message}</p>}
                  </div>
                </div>

                <div className="relative">
                  <h6 className="text-sm text-gray-500 mb-1">Email</h6>
                  <input
                    type="email"
                    {...register("email", {
                      onBlur: (e) => checkEmailExists(e.target.value),
                    })}
                    className={inputClass(!!errors.email || !!emailError)}
                    placeholder="Email"
                  />
                  <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                  {(errors.email || emailError) && (
                    <p className="mt-1 text-sm text-red-500">{emailError || errors.email?.message}</p>
                  )}
                </div>

                <div className="relative">
                  <h6 className="text-sm text-gray-500 mb-1">Phone</h6>
                  <input type="tel" {...register("phone")} className={inputClass(!!errors.phone)} placeholder="Phone" />
                  <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                  {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone.message}</p>}
                </div>
              </div>
            )}

            {step === "password" && (
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-gray-900">Set your password</h3>

                <div className="relative">
                  <h6 className="text-sm text-gray-500 mb-1">Password</h6>
                  <input
                    type="password"
                    {...register("password")}
                    autoComplete="new-password"
                    className={inputClass(!!errors.password)}
                    placeholder="Password"
                  />
                  <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
                  <ul className="mt-2 space-y-0.5 text-sm">
                    <li className={/[A-Z]/.test(passwordValue) ? "text-green-600" : "text-gray-400"}>
                      {/[A-Z]/.test(passwordValue) && <Check className="inline size-4 mr-1" />}
                      At least one uppercase letter
                    </li>
                    <li className={/[a-z]/.test(passwordValue) ? "text-green-600" : "text-gray-400"}>
                      {/[a-z]/.test(passwordValue) && <Check className="inline size-4 mr-1" />}
                      At least one lowercase letter
                    </li>
                    <li className={/[0-9]/.test(passwordValue) ? "text-green-600" : "text-gray-400"}>
                      {/[0-9]/.test(passwordValue) && <Check className="inline size-4 mr-1" />}
                      At least one number
                    </li>
                    <li className={/\W/.test(passwordValue) ? "text-green-600" : "text-gray-400"}>
                      {/\W/.test(passwordValue) && <Check className="inline size-4 mr-1" />}
                      At least one special character
                    </li>
                    <li className={passwordValue.length >= 8 ? "text-green-600" : "text-gray-400"}>
                      {passwordValue.length >= 8 && <Check className="inline size-4 mr-1" />}
                      At least 8 characters
                    </li>
                  </ul>
                  {submitError && (
                    <p role="alert" className="mt-3 text-sm text-red-600">
                      {submitError}
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === "success" && (
              <div className="space-y-4 text-center">
                <h3 className="text-2xl font-semibold text-gray-900">Success!</h3>
                <p className="text-gray-600">
                  We have securely saved your information. One of our representatives will contact you soon.
                </p>
              </div>
            )}

            {step !== "success" && (
              <div className="flex justify-center gap-4 pt-4">
                {step !== "property" && (
                  <button
                    type="button"
                    onClick={reverseStep}
                    className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-300 px-8 py-3 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all"
                  >
                    <ChevronLeft className="size-4" /> Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={advanceStep}
                  className="inline-flex items-center gap-1 rounded-full bg-[#9BFBE0] px-8 py-3 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all"
                >
                  {step !== "password" ? "Next" : "Submit"} <ChevronRight className="size-4" />
                </button>
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

MortgageShareRegister.propTypes = {};

export default MortgageShareRegister;
